import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

export interface TouristRoute {
  id: string;
  name: string;
  description: string;
  tourist_spots: string[];
  distance_km: number;
  estimated_time_min: number;
  difficulty: 'easy' | 'medium' | 'hard';
  highlights: string[];
  route_points: Array<{ lat: number; lng: number }>;
  scenic_rating: number;
  city: string;
  popularity_score?: number;
  created_at?: string;
}

export function useTouristRoutes() {
  const [routes, setRoutes] = useState<TouristRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTouristRoutes = async (
    lat: number,
    lng: number,
    distance?: number,
    scenicViews: boolean = true
  ) => {
    try {
      setLoading(true);
      setError(null);
      logger.info('[useTouristRoutes] Fetching tourist routes', { lat, lng, distance });

      const { data, error: fetchError } = await supabase.functions.invoke('suggest-tourist-routes', {
        body: { lat, lng, distance, scenicViews }
      });

      if (fetchError) {
        throw fetchError;
      }

      const touristRoutes = data?.routes || [];
      setRoutes(touristRoutes);
      
      logger.info('[useTouristRoutes] Fetched routes', { count: touristRoutes.length });
      
      return touristRoutes;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar rotas turísticas';
      setError(errorMessage);
      logger.error('[useTouristRoutes] Error fetching routes', err);
      
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: errorMessage,
      });
      
      return [];
    } finally {
      setLoading(false);
    }
  };

  const saveRoute = async (routeId: string) => {
    try {
      logger.info('[useTouristRoutes] Saving route', { routeId });

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Use untyped insert to avoid TypeScript errors with missing table types
      const { error: saveError } = await (supabase as any)
        .from('saved_tourist_routes')
        .insert({ route_id: routeId, user_id: user.id });

      if (saveError) {
        throw saveError;
      }

      toast({
        title: 'Rota salva!',
        description: 'A rota turística foi salva nos seus favoritos.',
      });
      
      return true;
    } catch (err) {
      logger.error('[useTouristRoutes] Error saving route', err);
      
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: 'Não foi possível salvar a rota.',
      });
      
      return false;
    }
  };

  const getSavedRoutes = async () => {
    try {
      setLoading(true);
      logger.info('[useTouristRoutes] Fetching saved routes');

      // Query raw SQL to avoid type issues
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('User not authenticated');
      }

      const { data, error: fetchError } = await (supabase as any)
        .from('tourist_routes')
        .select('*')
        .eq('created_by', userData.user.id);

      if (fetchError) {
        throw fetchError;
      }

      const savedRoutes = data || [];
      setRoutes(savedRoutes as TouristRoute[]);
      
      return savedRoutes;
    } catch (err) {
      logger.error('[useTouristRoutes] Error fetching saved routes', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    routes,
    loading,
    error,
    fetchTouristRoutes,
    saveRoute,
    getSavedRoutes,
  };
}
